// SagaBehavioralEvaluator — validates saga state transitions and compensation
// at runtime by observing the simulation event trace.
//
// FACET-ADR-018 §2: saga_transition and saga_compensation assertions.
// Moment owns structural validation (ADR-030). Facet owns behavioral validation.

import type { PlatformEvent } from '../../types/index.ts';
import type { AssertionType } from '../../types/simulation-results.ts';

// ---------------------------------------------------------------------------
// Saga definition (from saga-state-machine.json when available)
// ---------------------------------------------------------------------------

export interface SagaDefinition {
  sagaName: string;
  context: string;
  initialState: string;
  finalState: string;
  states: SagaStateEntry[];
  transitions: SagaTransition[];
  compensation?: string;
  timeout?: string;
  earlyExitStates?: string[];
}

export interface SagaStateEntry {
  name: string;
  isInitial: boolean;
  isFinal: boolean;
}

export interface SagaTransition {
  from: string;
  to: string;
  /**
   * Domain event that drives this transition (UPGRADED artifacts only —
   * feature-detected; absent in old saga-state-machines.json). When present
   * anywhere in a definition, saga progression is verified against the
   * DOMAIN events; hops whose transition lacks onEvent yield explicit skips.
   */
  onEvent?: string;
}

// Reserved synthetic states appended by Moment's timeout scenarios. They are
// never part of the declared `states`/`transitions` — their legality derives
// from the definition's `timeout`/`compensation` fields.
export const TIMED_OUT_STATE = 'TimedOut';
export const COMPENSATED_STATE = 'Compensated';

/** Timeout declared on the definition (feature-detected, empty ≠ declared). */
function hasTimeout(def: SagaDefinition): boolean {
  return typeof def.timeout === 'string' && def.timeout.length > 0;
}

/**
 * Compensation declared on the definition. The upgraded contract uses the
 * literal 'none' for "no compensation" — treated exactly like absence.
 */
function hasCompensation(def: SagaDefinition): boolean {
  return (
    typeof def.compensation === 'string' &&
    def.compensation.length > 0 &&
    def.compensation !== 'none'
  );
}

// ---------------------------------------------------------------------------
// Assertion output
// ---------------------------------------------------------------------------

export interface SagaAssertionOutput {
  assertionId: string;
  assertionType: AssertionType;
  passed: boolean;
  /** True when the tier could not be evaluated (no definitions available). */
  skipped?: boolean;
  detail: string;
  sagaName: string;
}

// ---------------------------------------------------------------------------
// Evaluator state — tracks saga progress across the trace
// ---------------------------------------------------------------------------

interface SagaTracker {
  sagaName: string;
  states: string[];
  lastStepIndex: number;
  /**
   * Index (in the event array) of the last domain event consumed by an
   * onEvent progression check — enforces that onEvent occurrences appear in
   * path order, not merely anywhere in the trace.
   */
  onEventCursor: number;
}

// ---------------------------------------------------------------------------
// Parse SimSaga event payload
// ---------------------------------------------------------------------------

interface SagaEventPayload {
  sagaName: string;
  state: string;
  triggeredBy?: string;
  /** Timeout name carried by SimSaga.<Saga>.TimedOut markers (upgraded artifacts). */
  timeoutName?: string;
}

function parseSagaEvent(event: PlatformEvent): SagaEventPayload | null {
  if (!event.eventType.startsWith('SimSaga.')) return null;

  const payload = event.payload as Record<string, unknown>;
  const sagaName = typeof payload.sagaName === 'string' ? payload.sagaName : null;
  const state = typeof payload.state === 'string' ? payload.state : null;
  const extras = {
    triggeredBy: typeof payload.triggeredBy === 'string' ? payload.triggeredBy : undefined,
    timeoutName: typeof payload.timeoutName === 'string' ? payload.timeoutName : undefined,
  };

  if (!sagaName || !state) {
    // Try parsing from eventType: SimSaga.{sagaName}.{state}
    const parts = event.eventType.split('.');
    if (parts.length >= 3) {
      return {
        sagaName: parts[1],
        state: parts.slice(2).join('.'),
        ...extras,
      };
    }
    return null;
  }

  return { sagaName, state, ...extras };
}

// ---------------------------------------------------------------------------
// Ended-as-observed detection (onEvent-bearing definitions only)
//
// Real chains encode ALTERNATIVE outcomes in one linear declaration (e.g.
// ticketwave SeatHoldLifecycle: Held -> Converting -> Converted -> Expired,
// where Expired is the expiry alternative — a converted hold correctly never
// expires). For definitions that carry >=1 onEvent transition, the saga ends
// "as observed" at its last verified milestone: when every transition beyond
// the observed last state is unmapped (no onEvent) or its onEvent never
// occurred, the chain tail is simply not represented in this scenario —
// SKIP, not FAIL. Definitions without any onEvent keep legacy behavior.
// ---------------------------------------------------------------------------

/** True when the definition carries at least one onEvent-bearing transition. */
function hasOnEventTransitions(def: SagaDefinition): boolean {
  return (def.transitions ?? []).some((t) => typeof t.onEvent === 'string');
}

/** All declared transitions reachable (transitively) from `fromState`. */
function transitionsBeyond(def: SagaDefinition, fromState: string): SagaTransition[] {
  const out: SagaTransition[] = [];
  const visited = new Set<string>([fromState]);
  const queue = [fromState];
  while (queue.length > 0) {
    const state = queue.shift()!;
    for (const t of def.transitions ?? []) {
      if (t.from !== state) continue;
      out.push(t);
      if (!visited.has(t.to)) {
        visited.add(t.to);
        queue.push(t.to);
      }
    }
  }
  return out;
}

/**
 * The chain tail past `lastState` is unfired/unmapped: there IS a tail, and
 * every transition in it either declares no onEvent (unverifiable) or its
 * onEvent never occurs after `searchAfterIndex` (the last domain event
 * consumed by the observed progression). A mapped tail transition whose
 * onEvent DID occur means the saga should have advanced — not this case.
 */
function chainTailUnfired(
  def: SagaDefinition,
  lastState: string,
  events: PlatformEvent[],
  searchAfterIndex: number,
): boolean {
  // Only a DECLARED observed state can end "as observed" — synthetic or
  // unknown states fall through to legacy handling.
  if (!(def.states ?? []).some((s) => s.name === lastState)) return false;
  const remaining = transitionsBeyond(def, lastState);
  if (remaining.length === 0) return false;
  const tail = events.slice(searchAfterIndex + 1);
  return remaining.every(
    (t) => typeof t.onEvent !== 'string' || !tail.some((e) => e.eventType === t.onEvent),
  );
}

/**
 * Recompute the onEvent cursor for an observed state path (mirrors the
 * progression check's in-order consumption) — used by the compensation
 * evaluator, whose tracker does not maintain one.
 */
function replayOnEventCursor(
  def: SagaDefinition,
  states: string[],
  events: PlatformEvent[],
): number {
  let cursor = -1;
  for (let k = 1; k < states.length; k++) {
    const t = (def.transitions ?? []).find(
      (tr) => tr.from === states[k - 1] && tr.to === states[k],
    );
    if (!t || typeof t.onEvent !== 'string') continue;
    for (let j = cursor + 1; j < events.length; j++) {
      if (events[j].eventType === t.onEvent) {
        cursor = j;
        break;
      }
    }
  }
  return cursor;
}

// ---------------------------------------------------------------------------
// Evaluate saga transitions across a full trace
// ---------------------------------------------------------------------------

/**
 * Evaluate all saga behavioral assertions for a complete event trace.
 *
 * When saga definitions are provided, validates transitions against the
 * declared state chain. Without definitions there is no ground truth: if
 * saga events are present, ONE explicit skip record is emitted for the tier
 * instead of heuristic passes.
 *
 * Instance-conflation guard: when a saga's final (or early-exit) state is
 * followed by its initial state, a NEW saga instance has started — the
 * tracker is split so the restart is not flagged as an invalid transition.
 */
export function evaluateSagaTransitions(
  events: PlatformEvent[],
  definitions?: SagaDefinition[],
): SagaAssertionOutput[] {
  const results: SagaAssertionOutput[] = [];
  const trackers = new Map<string, SagaTracker>();
  const defMap = new Map<string, SagaDefinition>();

  if (!definitions || definitions.length === 0) {
    // No ground truth — heuristic validation cannot fail meaningfully.
    // Emit one explicit skip for the tier when there is something to check.
    const hasSagaEvents = events.some((e) => parseSagaEvent(e) !== null);
    if (!hasSagaEvents) return [];
    return [
      {
        assertionId: 'saga-transition-defs-missing',
        assertionType: 'saga_transition',
        passed: false,
        skipped: true,
        detail:
          'Saga events are present but no saga definitions are available — saga transition tier skipped',
        sagaName: '',
      },
    ];
  }

  for (const def of definitions) {
    defMap.set(def.sagaName, def);
  }

  // Walk the trace and track saga state transitions
  for (let i = 0; i < events.length; i++) {
    const parsed = parseSagaEvent(events[i]);
    if (!parsed) continue;

    const { sagaName, state } = parsed;

    let tracker = trackers.get(sagaName);
    if (!tracker) {
      tracker = { sagaName, states: [], lastStepIndex: -1, onEventCursor: -1 };
      trackers.set(sagaName, tracker);
    }

    const def = defMap.get(sagaName);
    let previousState =
      tracker.states.length > 0 ? tracker.states[tracker.states.length - 1] : null;

    // Instance-conflation guard: a completed instance followed by the
    // initial state is a new instance, not a transition of the old one.
    // TimedOut/Compensated are completion states too (timeout scenarios).
    if (
      def &&
      previousState !== null &&
      state === def.initialState &&
      (previousState === def.finalState ||
        (def.earlyExitStates?.includes(previousState) ?? false) ||
        (hasTimeout(def) &&
          (previousState === TIMED_OUT_STATE || previousState === COMPENSATED_STATE)))
    ) {
      tracker.states = [];
      previousState = null;
    }

    tracker.states.push(state);
    tracker.lastStepIndex = i;

    if (def) {
      const declaredTransitions = def.transitions ?? [];
      // Progression verification activates only when the definition carries
      // onEvent-bearing transitions (upgraded artifact) — old artifacts keep
      // exactly the pre-existing structural-only behavior.
      const verifyProgression = declaredTransitions.some((t) => typeof t.onEvent === 'string');

      // With definitions: validate against declared transitions
      if (previousState) {
        const matchedTransition = declaredTransitions.find(
          (t) => t.from === previousState && t.to === state,
        );

        // Synthetic timeout states — evaluated only when the hop is NOT a
        // declared transition, so a (pathological) old definition with a real
        // "TimedOut"/"Compensated" state keeps exactly today's behavior.
        if (!matchedTransition && state === TIMED_OUT_STATE) {
          // TimedOut is a legal terminal transition from ANY state iff the
          // definition declares a timeout; when both names are present they
          // must match.
          if (!hasTimeout(def)) {
            results.push({
              assertionId: `saga-transition-${sagaName}-${i}`,
              assertionType: 'saga_transition',
              passed: false,
              detail: `Saga "${sagaName}" reported TimedOut (${previousState} -> ${TIMED_OUT_STATE}) but the definition declares no timeout`,
              sagaName,
            });
          } else if (parsed.timeoutName !== undefined && parsed.timeoutName !== def.timeout) {
            results.push({
              assertionId: `saga-transition-${sagaName}-${i}`,
              assertionType: 'saga_transition',
              passed: false,
              detail: `Saga "${sagaName}" timed out with timeoutName "${parsed.timeoutName}" but the definition declares timeout "${def.timeout}"`,
              sagaName,
            });
          } else {
            results.push({
              assertionId: `saga-transition-${sagaName}-${i}`,
              assertionType: 'saga_transition',
              passed: true,
              detail: `Saga "${sagaName}" timed out (${previousState} -> ${TIMED_OUT_STATE}) — declared timeout "${def.timeout}"`,
              sagaName,
            });
          }
          continue;
        }

        if (!matchedTransition && state === COMPENSATED_STATE && hasTimeout(def)) {
          // Compensated is legal only directly after TimedOut and only when
          // the definition declares compensation other than 'none'.
          const legal = previousState === TIMED_OUT_STATE && hasCompensation(def);
          results.push({
            assertionId: `saga-transition-${sagaName}-${i}`,
            assertionType: 'saga_transition',
            passed: legal,
            detail: legal
              ? `Saga "${sagaName}" compensated after timeout (${TIMED_OUT_STATE} -> ${COMPENSATED_STATE})`
              : previousState !== TIMED_OUT_STATE
                ? `Saga "${sagaName}" reported Compensated after "${previousState}" — Compensated is only legal directly after ${TIMED_OUT_STATE}`
                : `Saga "${sagaName}" reported Compensated but the definition declares no compensation`,
            sagaName,
          });
          continue;
        }

        const validTransition = matchedTransition !== undefined;
        results.push({
          assertionId: `saga-transition-${sagaName}-${i}`,
          assertionType: 'saga_transition',
          passed: validTransition,
          detail: validTransition
            ? `Saga "${sagaName}" valid transition: ${previousState} -> ${state}`
            : `Saga "${sagaName}" invalid transition: ${previousState} -> ${state} (not in declared transitions)`,
          sagaName,
        });

        // Progression check (upgraded artifacts): the transition's onEvent
        // domain event must precede this marker, in path order. Transitions
        // without onEvent yield an explicit skip — unverifiable, not silent.
        if (verifyProgression && matchedTransition) {
          if (typeof matchedTransition.onEvent === 'string') {
            let foundIndex = -1;
            for (let j = tracker.onEventCursor + 1; j < i; j++) {
              if (events[j].eventType === matchedTransition.onEvent) {
                foundIndex = j;
                break;
              }
            }
            if (foundIndex !== -1) {
              tracker.onEventCursor = foundIndex;
              results.push({
                assertionId: `saga-progression-${sagaName}-${i}`,
                assertionType: 'saga_transition',
                passed: true,
                detail: `Saga "${sagaName}" progression ${previousState} -> ${state} is backed by domain event "${matchedTransition.onEvent}" (event ${foundIndex})`,
                sagaName,
              });
            } else {
              results.push({
                assertionId: `saga-progression-${sagaName}-${i}`,
                assertionType: 'saga_transition',
                passed: false,
                detail: `Saga "${sagaName}" progression ${previousState} -> ${state} requires domain event "${matchedTransition.onEvent}" before the state marker, but it does not occur (in path order)`,
                sagaName,
              });
            }
          } else {
            results.push({
              assertionId: `saga-progression-${sagaName}-${i}`,
              assertionType: 'saga_transition',
              passed: false,
              skipped: true,
              detail: `Saga "${sagaName}" transition ${previousState} -> ${state} declares no onEvent — progression not verifiable for this hop`,
              sagaName,
            });
          }
        }
      } else {
        // First state should be the initial state
        const isInitial = def.initialState === state;
        results.push({
          assertionId: `saga-transition-${sagaName}-${i}`,
          assertionType: 'saga_transition',
          passed: isInitial,
          detail: isInitial
            ? `Saga "${sagaName}" started in initial state: ${state}`
            : `Saga "${sagaName}" started in "${state}" but expected initial state "${def.initialState}"`,
          sagaName,
        });
      }
    } else {
      // Saga observed in the trace but not declared in the definitions —
      // no ground truth for THIS saga; explicit skip (once per saga).
      if (tracker.states.length === 1) {
        results.push({
          assertionId: `saga-transition-${sagaName}-undeclared`,
          assertionType: 'saga_transition',
          passed: false,
          skipped: true,
          detail: `Saga "${sagaName}" appears in the trace but has no definition — transitions not verifiable`,
          sagaName,
        });
      }
    }
  }

  // Post-trace: check final state for sagas with definitions
  for (const [sagaName, tracker] of trackers) {
    const def = defMap.get(sagaName);
    if (!def) continue;

    const lastState = tracker.states[tracker.states.length - 1];
    const reachedFinalState = lastState === def.finalState;
    const isEarlyExit = def.earlyExitStates?.includes(lastState) ?? false;
    // Timeout scenarios legally terminate in TimedOut (or Compensated, when
    // it directly follows TimedOut). A missing Compensated while compensation
    // is declared is the compensation tier's FAIL, not a terminal-state one.
    const isTimeoutTerminal =
      hasTimeout(def) &&
      lastState !== def.finalState &&
      (lastState === TIMED_OUT_STATE ||
        (lastState === COMPENSATED_STATE &&
          tracker.states[tracker.states.length - 2] === TIMED_OUT_STATE));
    const passed = reachedFinalState || isEarlyExit || isTimeoutTerminal;

    // Ended-as-observed (onEvent-gated definitions ONLY — legacy defs keep
    // the final-state FAIL exactly as before): the chain tail past the last
    // verified milestone is unfired/unmapped, so the scenario never claimed
    // to reach the declared chain end — SKIP, not FAIL. A marker for a state
    // whose incoming onEvent never occurred still FAILs via the progression
    // records above; a fired-but-unadvanced tail still FAILs here.
    if (
      !passed &&
      hasOnEventTransitions(def) &&
      chainTailUnfired(def, lastState, events, tracker.onEventCursor)
    ) {
      results.push({
        assertionId: `saga-transition-${sagaName}-final`,
        assertionType: 'saga_transition',
        passed: false,
        skipped: true,
        detail: `Saga "${sagaName}" ended at "${lastState}"; remaining transitions unfired or unmapped — chain tail not represented in this scenario's events (declared chain end: "${def.finalState}")`,
        sagaName,
      });
      continue;
    }

    results.push({
      assertionId: `saga-transition-${sagaName}-final`,
      assertionType: 'saga_transition',
      passed,
      detail: reachedFinalState
        ? `Saga "${sagaName}" reached final state "${def.finalState}"`
        : isEarlyExit
          ? `Saga "${sagaName}" exited early in valid state "${lastState}"`
          : isTimeoutTerminal
            ? `Saga "${sagaName}" terminated via declared timeout "${def.timeout}" (last state: ${lastState})`
            : `Saga "${sagaName}" ended in "${lastState}" but expected final state "${def.finalState}"`,
      sagaName,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Evaluate saga compensation
// ---------------------------------------------------------------------------

/**
 * Check if saga compensation events are present when expected.
 * Requires saga definitions with compensation strings.
 *
 * This is a trace-level check: if the saga didn't reach its final state
 * and a compensation string is defined, we check if any subsequent event
 * could represent the compensation action.
 */
export function evaluateSagaCompensation(
  events: PlatformEvent[],
  definitions: SagaDefinition[],
): SagaAssertionOutput[] {
  const results: SagaAssertionOutput[] = [];
  const trackers = new Map<string, SagaTracker>();

  // Build saga state from trace
  for (let i = 0; i < events.length; i++) {
    const parsed = parseSagaEvent(events[i]);
    if (!parsed) continue;

    let tracker = trackers.get(parsed.sagaName);
    if (!tracker) {
      tracker = { sagaName: parsed.sagaName, states: [], lastStepIndex: -1, onEventCursor: -1 };
      trackers.set(parsed.sagaName, tracker);
    }
    tracker.states.push(parsed.state);
    tracker.lastStepIndex = i;
  }

  // Check each saga with a compensation defined ('none' — the upgraded
  // contract's explicit no-compensation marker — counts as not defined).
  for (const def of definitions) {
    if (!hasCompensation(def)) continue;

    const tracker = trackers.get(def.sagaName);
    if (!tracker) continue;

    // Timeout path (upgraded artifacts): a TimedOut marker replaces the
    // substring compensation sniff — the SimSaga.<Saga>.Compensated marker is
    // the ground truth. TimedOut with no subsequent Compensated while
    // compensation is declared is a FAIL.
    const timedOutIndex = tracker.states.lastIndexOf(TIMED_OUT_STATE);
    if (timedOutIndex !== -1) {
      const compensated = tracker.states.slice(timedOutIndex + 1).includes(COMPENSATED_STATE);
      results.push({
        assertionId: `saga-compensation-${def.sagaName}`,
        assertionType: 'saga_compensation',
        passed: compensated,
        detail: compensated
          ? `Saga "${def.sagaName}" timed out and compensated (${TIMED_OUT_STATE} -> ${COMPENSATED_STATE} marker observed)`
          : `Saga "${def.sagaName}" timed out but no ${COMPENSATED_STATE} marker followed — declared compensation "${def.compensation}" did not run`,
        sagaName: def.sagaName,
      });
      continue;
    }

    const lastState = tracker.states[tracker.states.length - 1];
    const reachedFinal = lastState === def.finalState;
    const isEarlyExit = def.earlyExitStates?.includes(lastState) ?? false;

    if (reachedFinal || isEarlyExit) {
      // Saga completed normally or exited via valid early state — no compensation expected
      results.push({
        assertionId: `saga-compensation-${def.sagaName}`,
        assertionType: 'saga_compensation',
        passed: true,
        detail: reachedFinal
          ? `Saga "${def.sagaName}" completed normally (reached ${def.finalState}), no compensation needed`
          : `Saga "${def.sagaName}" exited early in valid state "${lastState}", no compensation needed`,
        sagaName: def.sagaName,
      });
    } else if (
      hasOnEventTransitions(def) &&
      chainTailUnfired(def, lastState, events, replayOnEventCursor(def, tracker.states, events))
    ) {
      // Ended-as-observed (onEvent-gated definitions ONLY): the saga stopped
      // at its last verified milestone with the chain tail unfired/unmapped —
      // not abnormal incompleteness, so no compensation obligation arises.
      // Compensation obligations remain exactly the TimedOut-path semantics
      // (handled above); legacy defs keep the incompleteness sniff below.
      results.push({
        assertionId: `saga-compensation-${def.sagaName}`,
        assertionType: 'saga_compensation',
        passed: false,
        skipped: true,
        detail: `Saga "${def.sagaName}" ended at "${lastState}" as observed (chain tail unfired/unmapped) — no timeout occurred, compensation not triggered in this scenario`,
        sagaName: def.sagaName,
      });
    } else {
      // Saga didn't complete — compensation should have fired.
      // A compensating event must REFERENCE the saga (sagaName in the event
      // type or payload.sagaName) — a bare "cancel"/"release" substring
      // anywhere in the trace is not evidence for THIS saga's compensation.
      const hasCompensationEvent = events.slice(tracker.lastStepIndex + 1).some((e) => {
        const payload = e.payload as Record<string, unknown>;
        const referencesSaga =
          e.eventType.includes(def.sagaName) || payload.sagaName === def.sagaName;
        if (!referencesSaga) return false;
        return (
          e.eventType.toLowerCase().includes('compensat') ||
          e.eventType.toLowerCase().includes('release') ||
          e.eventType.toLowerCase().includes('cancel') ||
          e.eventType.toLowerCase().includes('revert') ||
          typeof payload.compensation === 'string'
        );
      });

      results.push({
        assertionId: `saga-compensation-${def.sagaName}`,
        assertionType: 'saga_compensation',
        passed: hasCompensationEvent,
        detail: hasCompensationEvent
          ? `Saga "${def.sagaName}" did not complete (last state: ${lastState}), compensation event detected`
          : `Saga "${def.sagaName}" did not complete (last state: ${lastState}), no compensation event found. Expected: "${def.compensation}"`,
        sagaName: def.sagaName,
      });
    }
  }

  return results;
}
